import React, { useState, useEffect } from "react";
import { Container, Paper, Typography, TextField, Modal, Box, Button, FormControl, InputLabel, Select, MenuItem, Tooltip } from "@mui/material";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function UpdateExamSubCategory() {
  const { state } = useLocation();
  const { subCategoryId } = useParams(); // Expects URL: /updateexamsubcategory/:subCategoryId
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [className, setClassName] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [selectedClassTypeId, setSelectedClassTypeId] = useState(state?.subCategoryData?.classTypeId || "");
  const [selectedExamCategoryId, setSelectedExamCategoryId] = useState(state?.subCategoryData?.examCategoryId || "");
  const [isWeightageDisabled, setIsWeightageDisabled] = useState(false);
  const [errors, setErrors] = useState({
    minMarks: '',
    maxMarks: ''
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");


  const [formData, setFormData] = useState({
    subExam: state?.subCategoryData?.subExam || "",
    minMarks: state?.subCategoryData?.minMarks || 0,
    maxMarks: state?.subCategoryData?.maxMarks || 0,
    weightage: state?.subCategoryData?.weightage || 0
  });

  const fetchClassddl = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ClassType/GetClassType`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data !== null) {
          setClassName(responseData);
        } else {
          console.error('No data found for classes');
        }
      } else {
        console.error('Failed to fetch class data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const fetchExamTypes = async (classTypeId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ExaminationSubCategory/Examddl_subcategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ classTypeId }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const filteredExamTypes = responseData.filter(item => item.isExamModeIndependent === 0);
        setExamTypes(filteredExamTypes);

        const selectedExamType = filteredExamTypes.find(item => item.examCriteriaId === selectedExamCategoryId);
        setIsWeightageDisabled(selectedExamType?.isExamWeightIndependent === 1);
      } else {
        console.error('Failed to fetch exam types data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const fetchExamSubCategory = async (examCriteriaId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ExaminationSubCategory/GetExamBySubCategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          examCategoryId: examCriteriaId,
          classTypeId: selectedClassTypeId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.data !== null) {
          setSubCategory(responseData);
        } else {
          setSubCategory([]);
          console.error('No data found for subcategories');
        }
      } else {
        console.error('Failed to fetch subcategory data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const handleClassTypeChange = (event) => {
    const classTypeId = event.target.value;
    setSelectedClassTypeId(classTypeId);
    fetchExamTypes(classTypeId);
    fetchExamSubCategory(selectedExamCategoryId);
  };

  const handleExamTypeChange = (event) => {
    const examCriteriaId = event.target.value;
    setSelectedExamCategoryId(examCriteriaId);
    const selectedExamType = examTypes.find(item => item.examCriteriaId === examCriteriaId);
    setIsWeightageDisabled(selectedExamType?.isExamWeightIndependent === 1);
    fetchExamSubCategory(examCriteriaId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (['minMarks', 'maxMarks', 'weightage'].includes(name)) {
      newValue = value === '' ? '' : Math.max(1, Math.min(100, Number(value)));
    }
    setFormData((prevData) => ({ ...prevData, [name]: newValue }));
    if (name === 'minMarks' || name === 'maxMarks') {
      validateMarks(name, newValue);
    }
  };

  const validateMarks = (field, value) => {
    let newErrors = { ...errors };
    const minMarks = field === 'minMarks' ? Number(value) : Number(formData.minMarks);
    const maxMarks = field === 'maxMarks' ? Number(value) : Number(formData.maxMarks);

    if (minMarks < 1) newErrors.minMarks = 'Min Marks must be at least 1';
    else newErrors.minMarks = '';

    if (maxMarks > 100) newErrors.maxMarks = 'Max Marks cannot exceed 100';
    else if (maxMarks <= minMarks) newErrors.maxMarks = 'Max Marks must be greater than Min Marks';
    else newErrors.maxMarks = '';

    setErrors(newErrors);
  };

  const totalWeightage = subCategory
    .filter(item => item.subCategoryId !== Number(subCategoryId))
    .reduce((sum, item) => sum + Number(item.weightage), 0);

  const isWeightageValid = (totalWeightage + Number(formData.weightage)) <= 100;

  const handleSave = async () => {
    if (!subCategoryId) {
      // alert('Subcategory ID is missing. Please navigate from the Exam SubCategory page.');
      setSnackbarMessage('Subcategory ID is missing. Please navigate from the Exam SubCategory page.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      navigate('/examsubcategory');
      return;
    }

    if (errors.minMarks || errors.maxMarks || !formData.minMarks || !formData.maxMarks || !isWeightageValid) {
      // alert("Please correct the errors or ensure total weightage does not exceed 100 before saving.");
      setSnackbarMessage('Please correct the errors or ensure total weightage does not exceed 100 before saving.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      return;
    }

    try {
      const apiUrl = `https://wsmsv1.aidigitalsec.com/api/ExaminationSubCategory/Id?Id=${subCategoryId}`;
      const token = sessionStorage.getItem('token');
      const requestData = {
        examCategoryId: selectedExamCategoryId,
        classTypeId: selectedClassTypeId,
        subExam: formData.subExam,
        minMarks: Number(formData.minMarks),
        maxMarks: Number(formData.maxMarks),
        weightage: Number(formData.weightage) || 0
      };

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        // alert('Data updated successfully');
        // navigate(-1);
        setShowSuccessModal(true);
      } else {
        const errorData = await response.json();
        console.error('Failed to update data:', errorData);
        // alert('Failed to update data. Please try again.');
        setSnackbarMessage('Failed to update data. Please try again.');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred while updating the data.');
       setSnackbarMessage('An error occurred while updating the data.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  useEffect(() => {
    fetchClassddl();
    if (selectedClassTypeId) {
      fetchExamTypes(selectedClassTypeId);
    }
    if (selectedExamCategoryId) {
      fetchExamSubCategory(selectedExamCategoryId);
    }
    if (!subCategoryId) {
      console.error('subCategoryId is undefined. Check URL or navigation.');
      // alert('Invalid subcategory ID. Redirecting to Exam SubCategory page.');
      setSnackbarMessage('Invalid subcategory ID. Redirecting to Exam SubCategory page.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      navigate('/examsubcategory');
    }
  }, [selectedClassTypeId, selectedExamCategoryId, subCategoryId, navigate]);

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: '#0B1F3D', // dark navy blue
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '2px auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Update Exam SubCategory
        </Typography> </Box>
      <Paper elevation={3} style={{ padding: "20px", textAlign: "center" }}>


        <FormControl fullWidth style={{ marginBottom: "20px" }}>
          <InputLabel htmlFor="classType">Class Type</InputLabel>
          <Select
            id="classType"
            value={selectedClassTypeId}
            onChange={handleClassTypeChange}
          >
            <MenuItem value=""><em>--Select--</em></MenuItem>
            {className.map((item) => (
              <MenuItem key={item.classTypeId} value={item.classTypeId}>
                {item.classType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth style={{ marginBottom: "20px" }}>
          <InputLabel htmlFor="examType">Exam Type</InputLabel>
          <Select
            id="examType"
            value={selectedExamCategoryId}
            onChange={handleExamTypeChange}
          >
            <MenuItem value=""><em>--Select--</em></MenuItem>
            {examTypes.map((item) => (
              <MenuItem key={item.examCriteriaId} value={item.examCriteriaId}>
                {item.examType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Sub Exam"
          value={formData.subExam}
          onChange={(e) => setFormData({ ...formData, subExam: e.target.value })}
          style={{ marginBottom: "20px" }}
        />

        <TextField
          fullWidth
          label="Min Marks"
          type="number"
          name="minMarks"
          value={formData.minMarks}
          onChange={handleInputChange}
          error={!!errors.minMarks}
          helperText={errors.minMarks}
          style={{ marginBottom: "20px" }}
        />

        <TextField
          fullWidth
          label="Max Marks"
          type="number"
          name="maxMarks"
          value={formData.maxMarks}
          onChange={handleInputChange}
          error={!!errors.maxMarks}
          helperText={errors.maxMarks}
          style={{ marginBottom: "20px" }}
        />

        <Tooltip title={`Weightage cannot exceed ${100 - totalWeightage} to keep total at or below 100`} placement="bottom">
          <TextField
            fullWidth
            label="Weightage"
            type="number"
            name="weightage"
            value={formData.weightage}
            onChange={handleInputChange}
            disabled={isWeightageDisabled}
            error={!isWeightageValid}
            helperText={!isWeightageValid ? `Weightage cannot exceed ${100 - totalWeightage}` : ''}
            style={{ marginBottom: "20px" }}
          />
        </Tooltip>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Typography variant="subtitle1">
            Weightage: {totalWeightage} out of 100
          </Typography>
          <div>
            <Button
              variant="contained"
              color="primary"
              style={{
                marginRight: "6px", width: "80px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}
              onClick={handleSave}
              disabled={!isWeightageValid || errors.minMarks || errors.maxMarks || !formData.minMarks || !formData.maxMarks}
            >
              <b>Update</b>
            </Button>
            <Button variant="contained" color="error" onClick={handleCancel} style={{
              width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight: 'bold'
            }}>
              <b>Cancel</b>
            </Button>
          </div>
        </div>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Exam SubCategory Updated ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Exam SubCategory Updated has been updated successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
<Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>

    </Container>
  );
}

export default UpdateExamSubCategory;
